/* Charms bracelet builder (sections/charms-bracelet-builder.liquid)

   Products come page by page from sections/charms-bracelet-feed.liquid through the Section
   Rendering API, so every price, currency and link is rendered by Shopify for the shopper's
   own market. The cart is Shopify's Ajax cart; every change asks for the same feed section
   rendered on /cart, so the page always works from the real cart (line keys, money-formatted
   totals, tags for the free-bracelet count) without formatting money in JavaScript.

   After each change the theme gets `theme:cart:refresh`, which updates the header count
   without opening the theme's drawer or its "item added" bar (both listen to
   `theme:product:added`, which this file never sends). */
(function () {
  'use strict';

  var root = document.querySelector('[data-cbb]');
  if (!root || root.dataset.cbbReady) return;
  root.dataset.cbbReady = '1';

  var CFG = JSON.parse(root.querySelector('[data-cbb-config]').textContent);
  var T = CFG.t;
  var G = CFG.gift || {};
  var THRESHOLD = Math.max(1, parseInt(G.threshold, 10) || 10);
  var FEED = CFG.feed;
  var ROOT_URL = String(CFG.routes.root || '/').replace(/\/?$/, '/');
  var jsUrl = function (u) { return /\.js$/.test(u) ? u : u + '.js'; };
  var ADD_URL = jsUrl(CFG.routes.add);
  var CHANGE_URL = jsUrl(CFG.routes.change);
  var UPDATE_URL = jsUrl(CFG.routes.update || (ROOT_URL + 'cart/update'));
  var CART_URL = CFG.routes.cart;
  var LINK_RATIO = 0.86; // a charm link is 0.86 times as tall as it is wide
  var FIRST_EAGER = 3;   // shelves fetched straight away; the rest load as they come near

  var $ = function (sel, ctx) { return (ctx || root).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || root).querySelectorAll(sel)); };
  var esc = function (v) {
    return String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  };
  var fill = function (str, map) {
    map = map || {};
    if (!('customers' in map)) map.customers = CFG.customers || '';
    return String(str || '').replace(/\[(\w+)\]/g, function (m, k) { return Object.prototype.hasOwnProperty.call(map, k) ? map[k] : m; });
  };
  var lower = function (list) {
    return (list || []).map(function (x) { return String(x).trim().toLowerCase(); }).filter(Boolean);
  };

  if (root.hasAttribute('data-cbb-hide-notification')) document.body.classList.add('cbb-page');
  root.style.setProperty('--cbb-slots', THRESHOLD);

  var BAG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 9h14l-1.2 11H6.2L5 9z"/><path d="M9 9V7a3 3 0 016 0v2"/><path d="M12 12.5v5M9.5 15h5"/></svg>';
  var BACK = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>';
  var SKEL = '<div class="cbb-skel" aria-hidden="true"><i></i><b></b><b></b><em></em></div>';

  /* ---------------------------------------------------------------- products */
  var SILVER = lower(CFG.metal && CFG.metal.silver);
  var GOLD = lower(CFG.metal && CFG.metal.gold);
  var HANG = String(CFG.hangTag || '').toLowerCase();
  var LOW = String(CFG.lowStock || '').toLowerCase();
  var HIDE = String(CFG.hideTag || '').toLowerCase(); // mystery boxes and the like never belong in a charm shelf
  var products = new Map();

  // Silver tags are checked first: a gold letter set on a silver link is a silver charm,
  // and rose gold counts as silver on this page.
  function metalOf(tagsLower, title) {
    if (tagsLower.some(function (t) { return SILVER.indexOf(t) > -1; })) return 'silver';
    if (tagsLower.some(function (t) { return GOLD.indexOf(t) > -1; })) return 'gold';
    var t = String(title || '').toLowerCase();
    if (/silver|rose gold/.test(t)) return 'silver';
    if (/gold/.test(t)) return 'gold';
    return '';
  }

  function prep(p) {
    if (products.has(p.id)) return products.get(p.id);
    var tags = lower(p.tags);
    p.metal = metalOf(tags, p.t);
    p.hang = !!HANG && tags.indexOf(HANG) > -1;
    p.charm = tags.indexOf(G.tag) > -1;
    p.skip = !!HIDE && tags.indexOf(HIDE) > -1;
    p.low = LOW ? ((p.tags || []).filter(function (t) { return String(t).toLowerCase().indexOf(LOW) > -1; })[0] || '') : '';
    p.off = p.c > p.p ? Math.round((1 - p.p / p.c) * 100) : 0;
    p.ar = p.ar > 0 ? p.ar : 1;
    products.set(p.id, p);
    return p;
  }

  /* -------------------------------------------------------------------- feed */
  var feedCache = new Map();

  function parseFeed(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var el = doc.querySelector('[data-cbb-feed]');
    return el ? JSON.parse(el.textContent) : { type: 'none' };
  }

  function getFeed(base, page, sort) {
    var u = new URL(base, location.origin);
    u.searchParams.set('section_id', FEED);
    u.searchParams.set('page', String(page));
    if (sort) u.searchParams.set('sort_by', sort);
    var key = u.pathname + u.search;
    if (!feedCache.has(key)) {
      var req = fetch(key, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
        .then(function (html) { var d = parseFeed(html); d.items = (d.items || []).map(prep); return d; });
      req.catch(function () { feedCache.delete(key); });
      feedCache.set(key, req);
    }
    return feedCache.get(key);
  }

  /* -------------------------------------------------------------------- cart */
  var cart = { items: [], count: 0, totalf: '' };
  var chain = Promise.resolve();
  var quietUntil = 0; // theme cart events caused by this page are ignored until then

  function setCart(d) {
    if (!d || d.type !== 'cart') return false;
    d.items.forEach(function (i) { i.tagsL = lower(i.tags); });
    cart = d;
    return true;
  }

  function loadCart() {
    return fetch(CART_URL + '?section_id=' + encodeURIComponent(FEED), { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) { setCart(parseFeed(html)); });
  }

  function cartPost(url, payload) {
    var body = Object.assign({ sections: FEED, sections_url: CART_URL }, payload);
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().catch(function () { return null; }).then(function (data) {
        if (!res.ok || !data || data.status) {
          var err = new Error((data && (data.description || data.message)) || 'HTTP ' + res.status);
          err.friendly = !!(data && data.description);
          throw err;
        }
        var html = data.sections && data.sections[FEED];
        if (html && setCart(parseFeed(html))) return data;
        return loadCart().then(function () { return data; });
      });
    });
  }

  function addVariant(id, quantity, properties) {
    var item = { id: id, quantity: quantity || 1 };
    if (properties) item.properties = properties;
    return cartPost(ADD_URL, { items: [item] });
  }
  function setLine(key, quantity) { return cartPost(CHANGE_URL, { id: key, quantity: Math.max(0, quantity) }); }

  // Every cart change runs one after another, so quick taps never race each other.
  // A cart change takes a moment, so the dock shows a spinner and stops taking taps until it lands.
  var busy = 0;
  function setBusy(on) {
    busy = Math.max(0, busy + (on ? 1 : -1));
    if (dock) dock.classList.toggle('is-busy', busy > 0);
  }

  function run(task) {
    setBusy(true);
    var p = chain.then(task).catch(function (err) {
      console.warn('[Charms bracelet builder]', err);
      toast(err && err.friendly ? err.message : T.error);
      return loadCart().then(function () { renderAll(false); }).catch(function () {});
    }).then(function (v) { setBusy(false); return v; });
    chain = p;
    return p;
  }

  function tellTheme() {
    quietUntil = Date.now() + 2000;
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
  }

  /* ------------------------------------------------------------ free bracelet */
  var isGift = function (i) { return !!i.gift && (!G.pid || i.pid === G.pid); };
  var giftLines = function () { return cart.items.filter(isGift); };
  var qualifies = function (i) { return !i.gift && i.tagsL.indexOf(G.tag) > -1 && (!HIDE || i.tagsL.indexOf(HIDE) < 0); };
  var wasUnlocked = null;

  // Charms in the order they were added (the cart lists the newest first).
  function unitsList() {
    var u = [];
    cart.items.slice().reverse().forEach(function (i) {
      if (qualifies(i)) for (var k = 0; k < i.q; k++) u.push(i);
    });
    return u;
  }

  function addGift(vid) { return addVariant(vid || G.vid, 1, { _free_gift: 'yes' }); }

  // Keeps exactly one free bracelet in the cart while the threshold is met and none below it.
  // Returns true when it changed the cart.
  function syncGift(opts) {
    opts = opts || {};
    if (!G.on || !G.vid) return Promise.resolve(false);
    var n = unitsList().length;
    var lines = giftLines();
    var changed = false;
    var step = Promise.resolve();

    if (n < THRESHOLD) {
      lines.forEach(function (l) { step = step.then(function () { changed = true; return setLine(l.key, 0); }); });
      wasUnlocked = false;
      return step.then(function () { return changed; });
    }

    if (lines.length) {
      if (lines[0].q > 1) step = step.then(function () { changed = true; return setLine(lines[0].key, 1); });
      lines.slice(1).forEach(function (l) { step = step.then(function () { changed = true; return setLine(l.key, 0); }); });
    } else if (G.mode === 'auto') {
      step = step.then(function () { changed = true; return addGift(G.vid); });
    } else if (opts.userAction && wasUnlocked === false) {
      openGift(); // the shopper just reached the threshold on this page: ask for the colour
    }
    wasUnlocked = true;
    return step.then(function () { return changed; });
  }

  function afterChange(opts) {
    opts = opts || {};
    return syncGift(opts).then(function () {
      renderAll(!!opts.pulse);
      tellTheme();
    });
  }

  function goCheckout(btn) {
    if (btn) btn.setAttribute('aria-busy', 'true');
    run(function () {
      var step = Promise.resolve();
      // with the popup, nothing may have been chosen yet: never lose the free bracelet
      if (G.on && G.vid && unitsList().length >= THRESHOLD && !giftLines().length) step = addGift(G.vid);
      return step.then(function () { location.href = ROOT_URL + 'checkout'; });
    });
  }

  /* ------------------------------------------------------------------- cards */
  var metal = 'all';
  var passMetal = function (p) { return metal === 'all' || p.metal === metal; };
  var qtyOf = function (vid) {
    return cart.items.reduce(function (n, i) { return n + (i.v === vid && !i.gift ? i.q : 0); }, 0);
  };

  function badges(p) {
    var b = [];
    if (p.off) b.push('<span class="cbb-bdg cbb-bdg--off">-' + p.off + '%</span>');
    if (p.low) b.push('<span class="cbb-bdg cbb-bdg--warn">' + esc(p.low) + '</span>');
    return b.length ? '<div class="cbb-badges">' + b.join('') + '</div>' : '';
  }

  function stepperHtml(id, q, prefix) {
    return '<div class="cbb-step"><button type="button" data-cbb-' + prefix + 'dec="' + id + '" aria-label="' + esc(T.removeOne) + '">&minus;</button>' +
      '<span data-cbb-q>' + esc(fill(T.inCart, { qty: q })) + '</span>' +
      '<button type="button" data-cbb-' + prefix + 'inc="' + id + '" aria-label="' + esc(T.addOne) + '">+</button></div>';
  }

  function cardHtml(p) {
    var q = qtyOf(p.v);
    var action = !p.av
      ? '<button type="button" class="cbb-atc" disabled>' + esc(T.soldOut) + '</button>'
      : '<button type="button" class="cbb-atc" data-cbb-add="' + p.id + '">' + BAG + esc(T.add) + '</button>' + stepperHtml(p.id, q, '');
    return '<article class="cbb-card' + (p.av ? '' : ' is-sold') + (q ? ' is-in' : '') + '" data-cbb-card="' + p.id + '">' +
      '<button type="button" class="cbb-media" data-cbb-qv="' + p.id + '" aria-label="' + esc(fill(T.quickView, { title: p.t })) + '">' +
        badges(p) + (p.img ? '<img src="' + esc(p.img) + '" alt="' + esc(p.t) + '" loading="lazy">' : '') +
      '</button>' +
      '<button type="button" class="cbb-title" data-cbb-qv="' + p.id + '"><span>' + esc(p.t) + '</span></button>' +
      '<div class="cbb-price">' + esc(p.pf) + (p.c ? '<s>' + esc(p.cf) + '</s>' : '') + '</div>' +
      action + '</article>';
  }

  // The watches tile is rendered by Liquid and each card carries its own variant id,
  // so it needs no feed data: only the "x in cart" line is kept in step with the cart.
  function syncWatches() {
    $$('[data-cbb-wcard]').forEach(function (c) {
      var q = qtyOf(+c.dataset.cbbWcard);
      c.classList.toggle('is-in', q > 0);
      var s = c.querySelector('[data-cbb-wq]');
      if (s) s.textContent = q ? fill(T.inCart, { qty: q }) : '';
    });
  }

  function addWatch(btn, variantId) {
    var label = btn.dataset.cbbLabel || btn.textContent.trim() || T.add;
    btn.dataset.cbbLabel = label;
    btn.classList.add('is-done');
    btn.textContent = '✓ ' + T.added;
    run(function () {
      return addVariant(variantId, 1).then(function () { return afterChange({ pulse: true }); });
    }).then(function () {
      btn.classList.remove('is-done');
      btn.textContent = label;
    });
  }

  function syncCards() {
    $$('[data-cbb-card]').forEach(function (c) {
      var p = products.get(+c.dataset.cbbCard);
      if (!p) return;
      var q = qtyOf(p.v);
      c.classList.toggle('is-in', q > 0);
      var s = c.querySelector('[data-cbb-q]');
      if (s) s.textContent = fill(T.inCart, { qty: q });
    });
  }

  /* ----------------------------------------------------------------- shelves */
  var shelves = (CFG.shelves || []).map(function (s) {
    var el = root.querySelector('[data-cbb-shelf="' + s.id + '"]');
    if (!el) return null;
    return Object.assign({}, s, {
      el: el,
      row: el.querySelector('[data-cbb-row]'),
      wrap: el.querySelector('.cbb-rowwrap'),
      bar: el.querySelector('.cbb-rbar i'),
      page: 0, pages: 1, items: [], ids: new Set(), loading: false, started: false, failed: false
    });
  }).filter(Boolean);
  var shelfById = new Map(shelves.map(function (s) { return [s.id, s]; }));

  function updRow(sh) {
    var row = sh.row;
    var max = row.scrollWidth - row.clientWidth;
    var frac = row.scrollWidth ? row.clientWidth / row.scrollWidth : 1;
    if (sh.bar) {
      sh.bar.style.width = Math.max(18, Math.min(100, frac * 100)) + '%';
      var f = Math.max(0.18, frac);
      sh.bar.style.transform = 'translateX(' + (max > 0 ? (row.scrollLeft / max) * (100 / f - 100) : 0) + '%)';
    }
    sh.wrap.classList.toggle('is-start', row.scrollLeft < 8);
    sh.wrap.classList.toggle('is-end', row.scrollLeft > max - 8);
  }

  function settleShelf(sh) {
    sh.row.querySelectorAll('.cbb-skel').forEach(function (x) { x.remove(); });
    var any = !!sh.row.querySelector('.cbb-card');
    sh.el.hidden = !any && (sh.page >= sh.pages || sh.failed);
    updRow(sh);
  }

  function fillShelf(sh, want) {
    if (sh.loading) return Promise.resolve();
    want = want || 8;
    sh.loading = true;
    sh.started = true;
    if (sh.row.querySelector('.cbb-card')) sh.row.insertAdjacentHTML('beforeend', SKEL + SKEL);
    var added = 0;
    var next = function () {
      if (added >= want || sh.page >= sh.pages) return Promise.resolve();
      return getFeed(sh.url, sh.page + 1, sh.sort).then(function (d) {
        sh.page = d.page || sh.page + 1;
        sh.pages = d.pages || sh.page;
        var fresh = (d.items || []).filter(function (p) { return p.av && !p.skip && !sh.ids.has(p.id); });
        fresh.forEach(function (p) { sh.ids.add(p.id); sh.items.push(p); });
        var vis = fresh.filter(passMetal);
        if (vis.length) {
          sh.row.querySelectorAll('.cbb-skel').forEach(function (x) { x.remove(); });
          sh.row.insertAdjacentHTML('beforeend', vis.map(cardHtml).join(''));
          added += vis.length;
        }
        return next();
      });
    };
    return next().catch(function (e) {
      sh.failed = true;
      console.warn('[Charms bracelet builder] shelf', sh.title, e);
    }).then(function () {
      sh.loading = false;
      settleShelf(sh);
    });
  }

  function rerenderShelves() {
    shelves.forEach(function (sh) {
      if (!sh.started) return;
      var vis = sh.items.filter(passMetal);
      sh.row.innerHTML = vis.map(cardHtml).join('');
      sh.row.scrollLeft = 0;
      sh.el.hidden = false;
      if (vis.length < 6 && sh.page < sh.pages) fillShelf(sh, 8 - vis.length);
      else settleShelf(sh);
    });
  }

  shelves.forEach(function (sh) {
    sh.row.addEventListener('scroll', function () {
      updRow(sh);
      if (sh.row.scrollLeft + sh.row.clientWidth > sh.row.scrollWidth - sh.row.clientWidth * 0.7) fillShelf(sh, 10);
    }, { passive: true });
  });

  var shelfObserver = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      shelfObserver.unobserve(e.target);
      var sh = shelves.filter(function (s) { return s.el === e.target; })[0];
      if (sh && !sh.started) fillShelf(sh);
    });
  }, { rootMargin: '700px 0px' }) : null;

  shelves.forEach(function (sh, i) {
    if (i < FIRST_EAGER || !shelfObserver) fillShelf(sh);
    else shelfObserver.observe(sh.el);
  });

  // An app popup can lock page scrolling (the scratch card one does), and then the observer
  // above never fires and every row below stays empty. So the rest of the shelves also fill
  // themselves in the background, one at a time, whether the shopper scrolls or not.
  var queued = [];
  var queueBusy = false;

  function runQueue() {
    if (queueBusy) return;
    var sh = null;
    while (queued.length && !sh) {
      var candidate = queued.shift();
      if (!candidate.started) sh = candidate;
    }
    if (!sh) return;
    queueBusy = true;
    fillShelf(sh).then(function () {
      queueBusy = false;
      setTimeout(runQueue, 400);
    });
  }

  setTimeout(function () {
    shelves.forEach(function (sh) { if (!sh.started) queued.push(sh); });
    runQueue();
  }, 2500);

  /* -------------------------------------------------------------- theme grid */
  var overview = $('[data-cbb-overview]');
  var gridBox = $('[data-cbb-grid]');
  var browse = $('[data-cbb-browse]');
  var sortSel = $('[data-cbb-sort]');
  var view = null;
  var overviewY = 0;

  function headerOffset() {
    var raw = getComputedStyle(document.body).getPropertyValue('--header-sticky-height') ||
      getComputedStyle(document.documentElement).getPropertyValue('--header-sticky-height');
    var v = parseFloat(raw);
    return isNaN(v) ? 0 : v;
  }

  function markTheme(id) {
    $$('[data-cbb-theme]').forEach(function (b) { b.classList.toggle('is-on', b.dataset.cbbTheme === id); });
  }

  function keepHtml(id) {
    var cats = $$('[data-cbb-cats] .cbb-cat').filter(function (c) {
      return c.dataset.cbbTheme !== id && c.dataset.cbbTheme !== 'all';
    }).slice(0, 6).map(function (c) {
      var k = c.cloneNode(true);
      k.classList.remove('is-on');
      k.removeAttribute('data-shopify-editor-block');
      return k.outerHTML;
    }).join('');
    return '<div class="cbb-keep"><h3>' + esc(T.keep) + '</h3>' + (cats ? '<div class="cbb-cats">' + cats + '</div>' : '') +
      '<button type="button" class="cbb-back" data-cbb-back>' + BACK + esc(T.back) + '</button></div>';
  }

  function openTheme(id, opts) {
    opts = opts || {};
    var sh = shelfById.get(id);
    if (!sh) return;
    closeOverlays();
    if (!view) overviewY = scrollY;
    view = { sh: sh, page: 0, pages: 1, total: 0, items: [], ids: new Set(), sort: (sortSel && sortSel.value) || sh.sort || '', loading: false };
    markTheme(id);
    overview.hidden = true;
    gridBox.hidden = false;
    gridBox.innerHTML =
      '<div class="cbb-backbar"><button type="button" class="cbb-back" data-cbb-back>' + BACK + esc(T.back) + '</button>' +
      '<h2><span>' + esc(sh.title) + '</span></h2><span class="cbb-back cbb-back--ghost" aria-hidden="true">' + BACK + esc(T.back) + '</span></div>' +
      '<div class="cbb-grid" data-cbb-grid-list>' + SKEL + SKEL + SKEL + SKEL + '</div><div data-cbb-grid-more></div>' + keepHtml(id);
    if (!opts.fromHistory) { try { history.pushState({ cbbTheme: id }, ''); } catch (e) { /* history unavailable */ } }
    if (!opts.keepScroll) scrollTo({ top: browse.getBoundingClientRect().top + scrollY - headerOffset() - 12, behavior: 'smooth' });
    return loadGrid();
  }

  function gridFooter() {
    var box = $('[data-cbb-grid-more]', gridBox);
    var list = $('[data-cbb-grid-list]', gridBox);
    if (!box || !list || !view) return;
    var shown = list.querySelectorAll('[data-cbb-card]').length;
    var h = '';
    if (!shown && view.page >= view.pages) h += '<div class="cbb-empty">' + esc(T.empty) + '</div>';
    if (view.page < view.pages) h += '<button type="button" class="cbb-moreb" data-cbb-more>' + esc(T.showMore) + '</button>';
    if (shown && metal === 'all') {
      h += '<div class="cbb-progress">' + esc(view.page < view.pages
        ? fill(T.showing, { shown: shown, total: view.total })
        : fill(T.showingAll, { total: shown })) + '</div>';
    }
    box.innerHTML = h;
  }

  function loadGrid() {
    if (!view || view.loading) return Promise.resolve();
    var v = view;
    v.loading = true;
    var list = $('[data-cbb-grid-list]', gridBox);
    var added = 0;
    var next = function () {
      if (added >= 12 || v.page >= v.pages) return Promise.resolve();
      return getFeed(v.sh.url, v.page + 1, v.sort).then(function (d) {
        if (view !== v) return;
        v.page = d.page || v.page + 1;
        v.pages = d.pages || v.page;
        v.total = d.total || v.total;
        var fresh = (d.items || []).filter(function (p) { return !p.skip && !v.ids.has(p.id); });
        fresh.forEach(function (p) { v.ids.add(p.id); v.items.push(p); });
        var vis = fresh.filter(passMetal);
        list.querySelectorAll('.cbb-skel').forEach(function (x) { x.remove(); });
        if (vis.length) list.insertAdjacentHTML('beforeend', vis.map(cardHtml).join(''));
        added += vis.length;
        return next();
      });
    };
    return next().catch(function (e) {
      console.warn('[Charms bracelet builder] grid', e);
    }).then(function () {
      if (view !== v) return;
      v.loading = false;
      list.querySelectorAll('.cbb-skel').forEach(function (x) { x.remove(); });
      gridFooter();
    });
  }

  function regrid() {
    var list = $('[data-cbb-grid-list]', gridBox);
    if (!view || !list) return;
    var vis = view.items.filter(passMetal);
    list.innerHTML = vis.map(cardHtml).join('');
    if (vis.length < 12 && view.page < view.pages) loadGrid();
    else gridFooter();
  }

  function showOverview() {
    if (!view) return;
    view = null;
    gridBox.hidden = true;
    gridBox.innerHTML = '';
    overview.hidden = false;
    markTheme('all');
    scrollTo({ top: overviewY, behavior: 'auto' });
  }

  function goBack() {
    var st = null;
    try { st = history.state; } catch (e) { /* ignore */ }
    if (st && st.cbbTheme) history.back();
    else showOverview();
  }

  window.addEventListener('popstate', function (e) {
    var id = e.state && e.state.cbbTheme;
    if (id && shelfById.has(id)) openTheme(id, { fromHistory: true });
    else showOverview();
  });

  if (sortSel) sortSel.addEventListener('change', function () {
    if (view) openTheme(view.sh.id, { fromHistory: true, keepScroll: true });
  });

  function setMetal(m, scroll) {
    metal = m;
    $$('[data-cbb-metal]').forEach(function (b) {
      var on = b.dataset.cbbMetal === m;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    rerenderShelves();
    if (view) regrid();
    checkNudge();
    if (scroll) {
      var seg = $('.cbb-seg');
      scrollTo({ top: seg.getBoundingClientRect().top + scrollY - headerOffset() - 20, behavior: 'smooth' });
    }
  }

  /* ----------------------------------------------------------------- bracelet */
  var dock = $('[data-cbb-dock]');
  var linksEl = $('[data-cbb-links]');
  var shownKeys = [];

  function braceletHtml(list, ghost, pop) {
    var h = '';
    for (var k = 0; k < THRESHOLD; k++) {
      var i = list[k];
      if (!i) { h += '<div class="cbb-slot"><i>' + (k + 1) + '</i></div>'; continue; }
      var attrs = i.key ? ' data-cbb-rm="' + esc(i.key) + '" aria-label="' + esc(fill(T.remove, { title: i.t })) + '"' : ' tabindex="-1" aria-hidden="true"';
      h += '<button type="button" class="cbb-slot is-on' + (k === ghost ? ' is-ghost' : '') + (k === pop ? ' is-pop' : '') + '"' + attrs + '>' +
        (i.img ? '<img src="' + esc(i.img) + '" alt="">' : '') + '</button>';
    }
    return h;
  }

  // how far the longest pendant drops below the band, so the bar grows to fit it
  function hangDepth(list, el) {
    var slotW = (el.clientWidth - 6 - (THRESHOLD - 1)) / THRESHOLD;
    var max = 0;
    list.slice(0, THRESHOLD).forEach(function (i) { max = Math.max(max, ((i.ar || 1) - LINK_RATIO) * slotW); });
    return Math.ceil(max);
  }

  // The hint line sits above the checkout button and carries the "remove all" link.
  // It asks first, so a mis-tap next to Checkout cannot empty the bracelet.
  var hintEl = $('[data-cbb-hint]');
  var askTimer = null;

  function renderHint(hasCharms) {
    if (!hintEl) return;
    if (askTimer) { clearTimeout(askTimer); askTimer = null; }
    hintEl.innerHTML = '<span>' + esc(T.dockHint || '') + '</span>' +
      (hasCharms && T.clear ? '<button type="button" class="cbb-clear" data-cbb-clear>' + esc(T.clear) + '</button>' : '');
  }

  function askClear() {
    hintEl.innerHTML = '<span>' + esc(T.clearAsk) + '</span>' +
      '<span class="cbb-clear__ask">' +
        '<button type="button" class="cbb-clear cbb-clear--yes" data-cbb-clear-yes>' + esc(T.clearYes) + '</button>' +
        '<button type="button" class="cbb-clear" data-cbb-clear-no>' + esc(T.clearNo) + '</button>' +
      '</span>';
    askTimer = setTimeout(function () { renderHint(unitsList().length > 0); }, 8000);
  }

  function renderDock(pulse) {
    var units = unitsList();
    var n = units.length;
    var lines = giftLines();
    var left = Math.max(0, THRESHOLD - n);
    var unlocked = n >= THRESHOLD;
    var msgEl = $('[data-cbb-dock-msg]');
    var canChoose = G.on && G.mode === 'popup' && !!giftBox;

    if (!n) msgEl.textContent = fill(T.dockEmpty, { threshold: THRESHOLD });
    else if (left > 1) msgEl.textContent = fill(T.dockLeft, { left: left, threshold: THRESHOLD });
    else if (left === 1) msgEl.textContent = fill(T.dockLeftOne, { left: 1, threshold: THRESHOLD });
    else if (lines.length) {
      msgEl.innerHTML = esc(fill(T.dockDone, { variant: lines[0].vt || '' })) +
        (canChoose ? ' <button type="button" class="cbb-dock__change" data-cbb-open-gift>' + esc(T.dockChange) + '</button>' : '');
    } else if (canChoose) {
      msgEl.innerHTML = '<button type="button" class="cbb-dock__choose" data-cbb-open-gift>' + esc(T.dockChoose) + '</button>';
    } else msgEl.textContent = fill(T.dockDone, { variant: '' });

    $('[data-cbb-dock-n]').textContent = Math.min(n, THRESHOLD) + '/' + THRESHOLD;
    dock.classList.toggle('is-unlocked', unlocked);
    dock.classList.toggle('has-items', cart.count > 0);

    var shown = units.slice(0, THRESHOLD);
    var pop = -1;
    if (pulse) {
      for (var k = 0; k < shown.length; k++) { if (shownKeys[k] !== shown[k].key) { pop = k; break; } }
    }
    linksEl.innerHTML = braceletHtml(shown, -1, pop);
    linksEl.style.marginBottom = hangDepth(shown, linksEl) + 'px';
    shownKeys = shown.map(function (u) { return u.key; });

    var more = n - THRESHOLD;
    var extra = $('[data-cbb-extra]');
    extra.classList.toggle('is-show', more > 0);
    extra.textContent = more > 1 ? fill(T.dockExtra, { n: more }) : more === 1 ? fill(T.dockExtraOne, { n: 1 }) : '';

    renderHint(n > 0);
    $('[data-cbb-items]').textContent = fill(cart.count === 1 ? T.itemsOne : T.itemsOther, { count: cart.count });
    $('[data-cbb-total]').textContent = cart.totalf || '';
    if (!cart.count) setMini(false);
    if (pulse) { dock.classList.remove('is-pulse'); void dock.offsetWidth; dock.classList.add('is-pulse'); }
    updateDock();
  }

  function setMini(on) {
    if (on && !dock.classList.contains('has-items')) return;
    dock.classList.toggle('is-mini', on);
    var g = $('[data-cbb-grab]');
    if (g) g.setAttribute('aria-label', on ? T.expand : T.minimise);
    updateDock();
  }

  function updateDock() {
    var reached = browse.getBoundingClientRect().top < innerHeight * 0.7;
    var past = root.getBoundingClientRect().bottom < innerHeight - 60;
    var show = (reached || cart.count > 0) && !past;
    dock.classList.toggle('is-show', show);
    document.documentElement.style.setProperty('--cbb-dock-h', show ? dock.offsetHeight + 'px' : '0px');
    document.body.classList.toggle('cbb-dock-on', show);
    checkNudge();
  }

  (function dockDrag() {
    var y0 = null;
    dock.addEventListener('touchstart', function (e) { y0 = e.touches[0].clientY; }, { passive: true });
    dock.addEventListener('touchmove', function (e) {
      if (y0 === null) return;
      var dy = e.touches[0].clientY - y0;
      if (dock.classList.contains('has-items') && Math.abs(dy) > 8) {
        e.preventDefault();
        dock.style.transform = dy > 0 && !dock.classList.contains('is-mini') ? 'translateY(' + Math.min(dy, 80) + 'px)' : '';
      }
    }, { passive: false });
    dock.addEventListener('touchend', function (e) {
      if (y0 === null) return;
      var dy = e.changedTouches[0].clientY - y0;
      y0 = null;
      dock.style.transform = '';
      if (dy > 35) setMini(true);
      else if (dy < -35) setMini(false);
    });
  })();

  /* -------------------------------------------------------------------- nudge */
  var nudgeOff = false;
  function checkNudge() {
    var nudge = $('[data-cbb-nudge]');
    if (!nudge) return;
    var units = unitsList();
    var metals = [];
    units.forEach(function (u) { var m = metalOf(u.tagsL, u.t); if (metals.indexOf(m) < 0) metals.push(m); });
    var m = metals.length === 1 ? metals[0] : '';
    var show = !nudgeOff && metal === 'all' && units.length >= 2 && !!m && dock.classList.contains('is-show');
    if (show) {
      var label = m === 'gold' ? T.metalGold : T.metalSilver;
      $('[data-cbb-nudge-text]').textContent = fill(T.nudge, { metal: label });
      var yes = $('[data-cbb-nudge-yes]');
      yes.innerHTML = '<i class="cbb-sw cbb-sw--' + m + '"></i>' + esc(fill(T.nudgeBtn, { metal: label }));
      yes.dataset.m = m;
    }
    nudge.classList.toggle('is-on', show);
  }

  /* --------------------------------------------------------------- overlays */
  var qv = $('[data-cbb-qv-panel]'); // the cards carry data-cbb-qv too, so the panel has its own name
  var drawer = $('[data-cbb-drawer]');
  var giftBox = $('[data-cbb-gift]');
  var shade = $('[data-cbb-shade]');
  var payTpl = $('[data-cbb-pay]');
  var PAY = payTpl ? payTpl.innerHTML.trim() : '';
  var qvId = null;
  var lastFocus = null;

  function open(panel) {
    if (!panel) return;
    lastFocus = document.activeElement;
    [qv, drawer, giftBox].forEach(function (x) { if (x && x !== panel) { x.classList.remove('is-on'); if (x === giftBox) x.hidden = true; } });
    if (panel === giftBox) panel.hidden = false;
    panel.classList.add('is-on');
    shade.classList.add('is-on');
    document.documentElement.classList.add('cbb-lock');
    var f = panel.querySelector('[data-cbb-close]');
    if (f) f.focus({ preventScroll: true });
  }

  function closeOverlays() {
    var wasOpen = shade.classList.contains('is-on');
    qvId = null;
    [qv, drawer].forEach(function (x) { if (x) x.classList.remove('is-on'); });
    if (giftBox) { giftBox.classList.remove('is-on'); giftBox.hidden = true; }
    shade.classList.remove('is-on');
    document.documentElement.classList.remove('cbb-lock');
    if (wasOpen && lastFocus && lastFocus.focus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
  }

  function openGift() {
    if (!giftBox) return;
    var cur = giftLines()[0];
    if (cur) {
      var r = giftBox.querySelector('input[value="' + cur.v + '"]');
      if (r) r.checked = true;
    }
    open(giftBox);
  }

  if (giftBox) {
    giftBox.querySelector('[data-cbb-gift-add]').addEventListener('click', function () {
      var r = giftBox.querySelector('input:checked');
      var vid = r ? +r.value : G.vid;
      closeOverlays();
      run(function () {
        var step = Promise.resolve();
        giftLines().forEach(function (l) { if (l.v !== vid) step = step.then(function () { return setLine(l.key, 0); }); });
        return step.then(function () {
          if (!giftLines().some(function (l) { return l.v === vid; })) return addGift(vid);
        }).then(function () { renderAll(false); tellTheme(); });
      });
    });
  }

  var WHY_ICONS = [
    '<path d="M12 2.8l7.8 4.5v9.4L12 21.2l-7.8-4.5V7.3z"/><path d="M12 12l7.8-4.7M12 12v9.2M12 12L4.2 7.3"/>',
    '<path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6z"/><path d="M8.8 12.2l2.2 2.2 4.3-4.6"/>',
    '<rect x="3.5" y="9" width="17" height="11" rx="1.5"/><path d="M12 9v11M3.5 13h17M12 9c-1.5-4-6-4-5.5-1 .3 1.3 3 1 5.5 1zM12 9c1.5-4 6-4 5.5-1-.3 1.3-3 1-5.5 1z"/>',
    '<path d="M12 20.5s-7.5-4.4-7.5-10A4.3 4.3 0 0 1 12 7.6a4.3 4.3 0 0 1 7.5 2.9c0 5.6-7.5 10-7.5 10z"/>'
  ];

  function openQV(id) {
    var p = products.get(+id);
    if (!p) return;
    qvId = p.id;
    renderQV();
    open(qv);
  }

  function renderQV() {
    var p = products.get(qvId);
    if (!p) return;
    var q = qtyOf(p.v);
    var units = unitsList();
    var n = units.length;
    var mine = units.slice(0, THRESHOLD);
    var preview, ghost;
    if (q) {
      preview = mine;
      ghost = -1;
      for (var k = 0; k < mine.length; k++) { if (mine[k].v === p.v) { ghost = k; break; } }
    } else {
      preview = mine.concat([{ img: p.img, ar: p.ar, t: p.t }]).slice(0, THRESHOLD);
      ghost = n < THRESHOLD ? n : -1;
    }
    var msg = q
      ? '<b>' + esc(T.onBracelet) + '</b> ' + (q > 1 ? esc(fill(T.youHave, { qty: q })) : '')
      : n >= THRESHOLD
        ? '<b>' + esc(T.unlockedBold) + '</b> ' + esc(fill(T.unlockedText, { n: n + 1 }))
        : '<b>' + esc(T.previewBold) + '</b> ' + esc(fill(T.previewText, { n: n + 1, threshold: THRESHOLD }));
    var row = function (a, b) { return b ? '<li><span>' + esc(a) + '</span>' + esc(b) + '</li>' : ''; };
    var facts = !p.charm ? row(T.availability[0], p.av ? T.availability[1] : T.soldOut) :
      row(T.material[0], T.material[1]) +
      (p.metal ? row(T.finish, p.metal === 'gold' ? T.metalGold : T.metalSilver) : '') +
      row(T.size[0], T.size[1]) +
      row(T.type[0], p.hang ? T.type[2] : T.type[1]) +
      (p.hang ? row(T.pendant[0], T.pendant[1]) : '') +
      row(T.availability[0], p.av ? T.availability[1] : T.soldOut);

    var gv = G.variants || [];
    var pick = gv.filter(function (v) { return p.metal === 'gold' ? /gold/i.test(v.t) && !/rose/i.test(v.t) : /silver/i.test(v.t); })[0] || gv[0];
    var free = G.on && p.charm ? '<div class="cbb-qvfree">' + (pick && pick.img ? '<img src="' + esc(pick.img) + '" alt="">' : '') +
      '<div><b>' + esc(fill(T.freeTitle, { threshold: THRESHOLD })) + '</b>' + esc(fill(T.freeText, { threshold: THRESHOLD })) + '</div></div>' : '';
    var why = (T.why || []).map(function (w, i) {
      if (!w[0] && !w[1]) return '';
      return '<div><svg viewBox="0 0 24 24" aria-hidden="true">' + WHY_ICONS[i % 4] + '</svg><b>' + esc(fill(w[0])) + '</b><span>' + esc(fill(w[1])) + '</span></div>';
    }).join('');

    $('[data-cbb-qv-body]', qv).innerHTML =
      '<a class="cbb-qvimg" href="' + esc(p.u) + '">' + badges(p) + (p.img ? '<img src="' + esc(p.img) + '" alt="' + esc(p.t) + '">' : '') + '</a>' +
      '<h2><a class="cbb-qvname" href="' + esc(p.u) + '">' + esc(p.t) + '</a></h2>' +
      '<div class="cbb-qvprice">' + esc(p.pf) + (p.c ? '<s>' + esc(p.cf) + '</s><span class="cbb-bdg cbb-bdg--off">-' + p.off + '%</span>' : '') + '</div>' +
      '<ul class="cbb-facts">' + facts + '</ul>' +
      (p.charm ? '<div class="cbb-qvbr"><p>' + msg + '</p><div class="cbb-links" data-cbb-qv-links>' + braceletHtml(preview, ghost, -1) + '</div></div>' : '') +
      free + (why ? '<div class="cbb-qvwhy">' + why + '</div>' : '') +
      (PAY ? '<div class="cbb-qvpay"><p><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="10.5" width="14" height="10" rx="2"/><path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5"/></svg>' + esc(T.secure) + '</p><div class="cbb-pays">' + PAY + '</div></div>' : '');

    requestAnimationFrame(function () {
      var el = $('[data-cbb-qv-links]', qv);
      if (el) el.style.marginBottom = hangDepth(preview, el) + 'px';
    });

    var foot = !p.av
      ? '<button type="button" class="cbb-atc" disabled>' + esc(T.soldOut) + '</button>'
      : q ? stepperHtml(p.id, q, 'q')
        : '<button type="button" class="cbb-atc" data-cbb-qadd="' + p.id + '">' + BAG + esc(fill(T.addPrice, { price: p.pf })) + '</button>';
    // the product-page link sits above the button: at the very bottom a phone's own toolbar hides it
    $('[data-cbb-qv-foot]', qv).innerHTML = '<a class="cbb-full" href="' + esc(p.u) + '">' + esc(T.full) + '</a>' + foot;
  }

  function renderDrawer() {
    var list = $('[data-cbb-lines]');
    if (!cart.items.length) {
      list.innerHTML = '<div class="cbb-drawer__empty">' + esc(T.drawerEmpty) + '</div>';
    } else {
      list.innerHTML = cart.items.slice().reverse().map(function (i) {
        // the free bracelet is managed by the charm count, so only paid lines get a remove button
        var x = i.gift ? '' : '<button type="button" class="cbb-li__x" data-cbb-rmline="' + esc(i.key) +
          '" aria-label="' + esc(fill(T.removeLine, { title: i.t })) + '">&times;</button>';
        return '<div class="cbb-li' + (i.gift ? ' cbb-li--free' : '') + '">' + (i.img ? '<img src="' + esc(i.img) + '" alt="">' : '') +
          '<div class="cbb-li__t">' + esc(i.t) + (i.gift && i.vt ? ' &middot; ' + esc(i.vt) : '') +
          '<small>' + esc(fill(T.drawerQty, { qty: i.q })) + '</small></div>' +
          '<div class="cbb-li__p">' + (i.gift ? esc(T.free) : esc(i.lpf)) + '</div>' + x + '</div>';
      }).join('');
    }
    $('[data-cbb-drawer-total]').textContent = cart.totalf || '';
    var slot = $('[data-cbb-upsell-slot]');
    var tpl = $('[data-cbb-upsell]');
    if (slot) slot.innerHTML = tpl && unitsList().length >= THRESHOLD ? tpl.innerHTML : '';
    $$('[data-cbb-checkout]').forEach(function (b) { b.disabled = !cart.count; });
  }

  function renderAll(pulse) {
    syncCards();
    syncWatches();
    renderDock(!!pulse);
    renderDrawer();
    if (qvId !== null) renderQV();
  }

  /* --------------------------------------------------------------- actions */
  function inc(id) {
    var p = products.get(+id);
    if (!p) return;
    run(function () { return addVariant(p.v, 1).then(function () { return afterChange({ userAction: true, pulse: true }); }); });
  }

  function dec(id) {
    var p = products.get(+id);
    if (!p) return;
    run(function () {
      var line = cart.items.filter(function (i) { return i.v === p.v && !i.gift; })[0];
      if (!line) return renderAll(false);
      return setLine(line.key, line.q - 1).then(function () { return afterChange({ userAction: true }); });
    });
  }

  function removeKey(key) {
    run(function () {
      var line = cart.items.filter(function (i) { return i.key === key; })[0];
      if (!line) return renderAll(false);
      return setLine(key, line.q - 1).then(function () { return afterChange({ userAction: true }); });
    });
  }

  // the whole line at once, for the × in the cart drawer
  function removeLine(key) {
    run(function () {
      if (!cart.items.filter(function (i) { return i.key === key; })[0]) return renderAll(false);
      return setLine(key, 0).then(function () { return afterChange({ userAction: true }); });
    });
  }

  // "Remove all" empties the bracelet: every charm plus the free bracelet that hangs off it.
  // Watches, sets and anything else in the cart are left alone.
  // One /cart/update.js call does the lot: removing the lines one by one invalidates the
  // later line keys, and Shopify then answers "no valid id or line parameter" halfway through.
  function clearCharms() {
    var updates = {};
    var any = false;
    cart.items.forEach(function (i) { if (qualifies(i) || i.gift) { updates[i.v] = 0; any = true; } });
    if (!any) return renderDock(false);
    if (hintEl) hintEl.innerHTML = '<span class="cbb-working"><i></i>' + esc(T.clearBusy || '') + '</span>';
    run(function () {
      return cartPost(UPDATE_URL, { updates: updates }).then(function () { return afterChange({}); });
    });
  }

  function addFromCard(btn, id) {
    var p = products.get(+id);
    if (!p) return;
    btn.classList.add('is-done');
    btn.textContent = '✓ ' + T.added;
    run(function () {
      return addVariant(p.v, 1).then(function () { return afterChange({ userAction: true, pulse: true }); });
    }).then(function () {
      btn.classList.remove('is-done');
      btn.innerHTML = BAG + esc(T.add);
    });
  }

  function toggleFilters() {
    var layout = $('[data-cbb-layout]');
    layout.classList.toggle('is-nofilters');
    var l = $('[data-cbb-filters-label]');
    if (l) l.textContent = layout.classList.contains('is-nofilters') ? T.filtersShow : T.filtersHide;
  }

  var toastEl = null;
  var toastTimer = null;
  function toast(msg) {
    if (!msg) return;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'cbb-toast';
      toastEl.setAttribute('role', 'status');
      root.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove('is-on'); }, 3500);
  }

  root.addEventListener('click', function (e) {
    var t = e.target.closest('button');
    if (!t || !root.contains(t)) return;
    var d = t.dataset;
    if (d.cbbQv !== undefined) { e.preventDefault(); openQV(d.cbbQv); return; }
    if (d.cbbAdd !== undefined) { addFromCard(t, d.cbbAdd); return; }
    if (d.cbbAddv !== undefined) { addWatch(t, +d.cbbAddv); return; }
    if (d.cbbInc !== undefined || d.cbbQinc !== undefined || d.cbbQadd !== undefined) { inc(d.cbbInc || d.cbbQinc || d.cbbQadd); return; }
    if (d.cbbDec !== undefined || d.cbbQdec !== undefined) { dec(d.cbbDec || d.cbbQdec); return; }
    if (d.cbbTheme !== undefined) { e.preventDefault(); if (d.cbbTheme === 'all') goBack(); else openTheme(d.cbbTheme); return; }
    if (d.cbbBack !== undefined) { goBack(); return; }
    if (d.cbbMore !== undefined) { loadGrid(); return; }
    if (d.cbbRnav !== undefined) {
      var row = t.closest('.cbb-rowwrap').querySelector('[data-cbb-row]');
      row.scrollBy({ left: row.clientWidth * 0.8 * (+d.cbbRnav), behavior: 'smooth' });
      return;
    }
    if (d.cbbMetal !== undefined) { setMetal(d.cbbMetal); return; }
    if (d.cbbRm !== undefined) { t.classList.add('is-out'); setTimeout(function () { removeKey(d.cbbRm); }, 200); return; }
    if (d.cbbRmline !== undefined) { var li = t.closest('.cbb-li'); if (li) li.classList.add('is-out'); removeLine(d.cbbRmline); return; }
    if (d.cbbClear !== undefined) { askClear(); return; }
    if (d.cbbClearYes !== undefined) { clearCharms(); return; }
    if (d.cbbClearNo !== undefined) { renderHint(unitsList().length > 0); return; }
    if (d.cbbOpenCart !== undefined) { renderDrawer(); open(drawer); return; }
    if (d.cbbOpenGift !== undefined) { openGift(); return; }
    if (d.cbbCheckout !== undefined) { goCheckout(t); return; }
    if (d.cbbClose !== undefined) { closeOverlays(); return; }
    if (d.cbbFiltersToggle !== undefined) { toggleFilters(); return; }
    if (d.cbbNudgeYes !== undefined) { setMetal(t.dataset.m, true); return; }
    if (d.cbbNudgeNo !== undefined) { nudgeOff = true; checkNudge(); return; }
    if (d.cbbGrab !== undefined) { setMini(!dock.classList.contains('is-mini')); }
  });

  shade.addEventListener('click', closeOverlays);
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeOverlays(); });
  window.addEventListener('scroll', updateDock, { passive: true });

  // The strip slides by half the track, so the track has to be at least twice as wide as its
  // box or a gap appears at the end of each loop. Copy whole groups until it is.
  function fillStrip() {
    var strip = $('.cbb-watchtile__strip');
    if (!strip) return;
    var track = strip.querySelector('.cbb-watchtile__track');
    var groups = track ? track.querySelectorAll('.cbb-watchtile__grp') : null;
    if (!groups || !groups.length) return;
    var one = groups[0].getBoundingClientRect().width;
    var box = strip.getBoundingClientRect().width;
    if (!one || !box) return;
    var need = Math.ceil((box * 2) / one);
    if (need % 2) need++;
    need = Math.max(2, Math.min(need, 16));
    for (var i = groups.length; i < need; i++) track.appendChild(groups[i % groups.length].cloneNode(true));
  }
  fillStrip();
  window.addEventListener('resize', function () { renderDock(false); shelves.forEach(updRow); fillStrip(); });

  // The shopper can also change the cart in the theme's own drawer; follow along.
  var themeTimer = null;
  document.addEventListener('theme:cart:change', function () {
    if (Date.now() < quietUntil) return;
    clearTimeout(themeTimer);
    themeTimer = setTimeout(function () {
      run(function () {
        return loadCart().then(function () { return syncGift({}); }).then(function (changed) {
          renderAll(false);
          if (changed) tellTheme();
        });
      });
    }, 400);
  });

  window.addEventListener('pageshow', function (e) {
    if (e.persisted) run(function () { return loadCart().then(function () { renderAll(false); }); });
  });

  /* ------------------------------------------------------------------ reviews */
  (function reviews() {
    var box = $('[data-cbb-revs]');
    if (!box) return;
    var figs = $$('figure', box);
    var dots = $$('.cbb-dots button', box);
    if (figs.length < 2) return;
    var k = 0;
    var timer = null;
    var go = function (n) {
      figs[k].classList.remove('is-on');
      if (dots[k]) dots[k].classList.remove('is-on');
      k = (n + figs.length) % figs.length;
      figs[k].classList.add('is-on');
      if (dots[k]) dots[k].classList.add('is-on');
    };
    var start = function () { clearInterval(timer); timer = setInterval(function () { go(k + 1); }, 5000); };
    dots.forEach(function (d, i) { d.addEventListener('click', function () { go(i); start(); }); });
    var x0 = null;
    box.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; }, { passive: true });
    box.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 35) { go(k + (dx < 0 ? 1 : -1)); start(); }
      x0 = null;
    });
    start();
  })();

  /* --------------------------------------------------------------------- start */
  run(function () {
    return loadCart().then(function () {
      wasUnlocked = unitsList().length >= THRESHOLD;
      return syncGift({});
    }).then(function (changed) {
      renderAll(false);
      if (changed) tellTheme();
    });
  });
  updateDock();
})();
